import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Group,
  Modal,
  Paper,
  Progress,
  SegmentedControl,
  Select,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FiInfo, FiRefreshCw } from "react-icons/fi";
import { useProjectId } from "../hooks/useProjectId";
import { DrofusMappingLogicModal } from "./DrofusMappingLogicModal";
import { DrofusForekomsterTable } from "./DrofusForekomsterTable";
import { openapi } from "../../openapi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectDrofusForekomsterActiveView,
  selectDrofusForekomsterLatestRunId,
  selectDrofusForekomsterRuns,
  selectDrofusForekomsterRunsApiArg,
  selectDrofusForekomsterRunningRun,
  selectDrofusForekomsterSelectedRun,
  selectDrofusForekomsterSelectedRunId,
  selectDrofusForekomsterSelectedRunExists,
  setDrofusForekomsterActiveView,
  setDrofusForekomsterSelectedRunId,
} from "./drofusForekomsterSlice";
import { DrofusForekomsterFilterBar } from "./DrofusForekomsterFilterBar";
import { DrofusArticlesTable } from "./DrofusArticlesTable";
import { DrofusSyncJobButton } from "./DrofusSyncJobButton";
import { DrofusScheduleButton } from "./DrofusScheduleButton";
import { DrofusDeleteRunButton } from "./DrofusDeleteRunButton";
import { selectIsSystemAdmin } from "../userSelectors";

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);
}

function formatElapsedMs(value?: number): string {
  if (typeof value !== "number") return "-";
  if (value < 1000) return `${value} ms`;
  return `${(value / 1000).toFixed(1)} s`;
}

export const DrofusForekomsterPage: React.FC = () => {
  const projectNo = useProjectId();
  const dispatch = useAppDispatch();
  const selectedRunId = useAppSelector(selectDrofusForekomsterSelectedRunId);
  const selectedRun = useAppSelector(selectDrofusForekomsterSelectedRun);
  const selectedRunExists = useAppSelector(selectDrofusForekomsterSelectedRunExists);
  const latestRunId = useAppSelector(selectDrofusForekomsterLatestRunId);
  const runs = useAppSelector(selectDrofusForekomsterRuns);
  const runsApiArg = useAppSelector(selectDrofusForekomsterRunsApiArg);
  const runningRun = useAppSelector(selectDrofusForekomsterRunningRun);
  const activeView = useAppSelector(selectDrofusForekomsterActiveView);
  const userIsAdmin = useAppSelector(selectIsSystemAdmin);
  const [syncRequestPending, setSyncRequestPending] = useState(false);
  const [mappingModalOpen, { open: openMappingModal, close: closeMappingModal }] =
    useDisclosure(false);
  const [runDetailsOpen, { open: openRunDetails, close: closeRunDetails }] =
    useDisclosure(false);

  const {
    isLoading: runsLoading,
    isFetching: runsFetching,
    refetch: refetchRuns,
  } = openapi.endpoints.getMultiplacerDrofusInterfaceRuns.useQuery(runsApiArg ?? skipToken, {
    pollingInterval: runningRun || syncRequestPending ? 10000 : 0,
  });

  const syncInProgress = Boolean(runningRun) || syncRequestPending;

  const runOptions = runs
    .filter((run): run is typeof run & { _id: string } => typeof run._id === "string")
    .map((run) => ({
      value: run._id,
      label: `${formatDateTime(run.startedAt)} · ${run.status} · ${
        run.result?.savedSnapshotCount ?? run.result?.fetchedRowCount ?? 0
      } rader`,
    }));

  useEffect(() => {
    if (!latestRunId) return;

    if (!selectedRunExists) {
      dispatch(setDrofusForekomsterSelectedRunId(latestRunId));
    }
  }, [dispatch, latestRunId, selectedRunExists]);

  return (
    <Flex direction="column" h="100%" w="100%" p="md" gap="md" bg="gray.0">
      <DrofusMappingLogicModal opened={mappingModalOpen} onClose={closeMappingModal} />
      <Modal
        opened={runDetailsOpen}
        onClose={closeRunDetails}
        title="Kjøringsdetaljer"
        size="lg"
        centered
      >
        {selectedRun ? (
          <Box>
            <Group gap="xs" mb="sm">
              <Badge variant="light" color="blue" radius="sm">
                {selectedRun.status}
              </Badge>
              <Badge variant="light" color="gray" radius="sm">
                {selectedRun.trigger}
              </Badge>
              <Badge variant="light" color="gray" radius="sm">
                {selectedRun.entityType}
              </Badge>
            </Group>

            <Group gap="xl" align="flex-start" mb="md">
              <Box>
                <Text size="xs" c="dimmed">
                  Startet
                </Text>
                <Text size="sm" fw={600}>
                  {formatDateTime(selectedRun.startedAt)}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Ferdig
                </Text>
                <Text size="sm" fw={600}>
                  {formatDateTime(selectedRun.finishedAt)}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Varighet
                </Text>
                <Text size="sm" fw={600}>
                  {formatElapsedMs(selectedRun.elapsedMs)}
                </Text>
              </Box>
            </Group>

            <Group gap="xl" align="flex-start" mb="md">
              <Box>
                <Text size="xs" c="dimmed">
                  Hentet
                </Text>
                <Text size="sm" fw={600}>
                  {selectedRun.result?.fetchedRowCount ?? "-"}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Lagret
                </Text>
                <Text size="sm" fw={600}>
                  {selectedRun.result?.savedSnapshotCount ?? "-"}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Sider
                </Text>
                <Text size="sm" fw={600}>
                  {selectedRun.result?.pageCount ?? "-"}
                </Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">
                  Page size
                </Text>
                <Text size="sm" fw={600}>
                  {selectedRun.api.pageSize}
                </Text>
              </Box>
            </Group>

            <Text size="sm" fw={600} mb={4}>
              Komplett run-dokument
            </Text>
            <Code block style={{ maxHeight: 360, overflow: "auto" }}>
              {JSON.stringify(selectedRun, null, 2)}
            </Code>
          </Box>
        ) : (
          <Text size="sm" c="dimmed">
            Velg en kjøring først.
          </Text>
        )}
      </Modal>

      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box>
          <Group gap="xs" align="center">
            <Title order={3}>Forekomster</Title>
            <Badge variant="light" color="blue" radius="sm">
              dRofus
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Prosjekt {projectNo}
          </Text>
        </Box>

        <Group gap="xs" wrap="nowrap">
          {userIsAdmin && (
            <DrofusSyncJobButton
              projectNo={projectNo}
              disabled={!Number.isFinite(projectNo) || projectNo <= 0 || runsLoading}
              runningRunId={runningRun?._id ?? null}
              onStarted={() => void refetchRuns()}
              onPendingChange={setSyncRequestPending}
            />
          )}
          <DrofusScheduleButton
            projectNo={projectNo}
            disabled={!Number.isFinite(projectNo) || projectNo <= 0}
          />
          <Select
            size="xs"
            w={420}
            placeholder="Velg kjøring"
            data={runOptions}
            value={selectedRunId}
            onChange={(value) => dispatch(setDrofusForekomsterSelectedRunId(value))}
            searchable
            clearable
            disabled={runsLoading}
            nothingFoundMessage={runsLoading ? "Laster kjøringer..." : "Ingen kjøringer funnet"}
          />
          <Button
            size="xs"
            variant="subtle"
            radius="sm"
            leftSection={<FiInfo size={14} />}
            disabled={!selectedRun}
            onClick={openRunDetails}
          >
            Detaljer
          </Button>
          <DrofusDeleteRunButton
            projectNo={projectNo}
            selectedRun={selectedRun}
            disabled={!Number.isFinite(projectNo) || projectNo <= 0 || runsLoading}
            onDeleted={() => void refetchRuns()}
          />
          <Button size="xs" variant="subtle" radius="sm" onClick={openMappingModal}>
            Mappinglogikk
          </Button>
          <Button
            size="xs"
            variant="light"
            radius="sm"
            leftSection={<FiRefreshCw size={14} />}
            loading={runsFetching && !runsLoading}
            onClick={() => refetchRuns()}
          >
            Hent data
          </Button>
        </Group>
      </Group>

      <Paper withBorder radius="sm" p="xs">
        <Flex direction="column" gap="xs">
          <Group justify="space-between" wrap="nowrap">
            <SegmentedControl
              size="sm"
              color="blue"
              value={activeView}
              onChange={(value) =>
                dispatch(setDrofusForekomsterActiveView(value as "occurrences" | "articles"))
              }
              data={[
                { value: "occurrences", label: "Forekomster" },
                { value: "articles", label: "Artikler" },
              ]}
              styles={{
                root: {
                  border: "1px solid var(--mantine-color-blue-3)",
                  backgroundColor: "var(--mantine-color-blue-0)",
                },
                label: {
                  fontWeight: 700,
                },
              }}
            />
            <Text size="sm" c="dimmed">
              {activeView === "articles"
                ? "Artikler grupperes på artikkel-ID, compCode og navn"
                : "Forekomster viser enkeltplasseringer i rom"}
            </Text>
          </Group>

          {syncInProgress && (
            <Box>
              <Group justify="space-between" mb={4}>
                <Text size="xs" fw={700} c="blue">
                  {runningRun?.status === "queued" ? "dRofus-jobb i kø" : "dRofus-jobb kjører"}
                </Text>
                <Text size="xs" c="dimmed">
                  {runningRun
                    ? `${runningRun.status === "queued" ? "Køet" : "Startet"} ${formatDateTime(runningRun.startedAt)}`
                    : "Starter jobb..."}
                </Text>
              </Group>
              <Progress value={100} size="sm" radius="xl" animated striped />
            </Box>
          )}
        </Flex>
      </Paper>

      <Flex gap="sm" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <DrofusForekomsterFilterBar />
        <Paper withBorder radius="sm" p="sm" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          {activeView === "articles" ? <DrofusArticlesTable /> : <DrofusForekomsterTable />}
        </Paper>
      </Flex>
    </Flex>
  );
};
